import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import styles from './TopPage.module.css';

const assetBase = '/dropai-renew-2605-top/Asset';
const appStoreUrl = 'https://apps.apple.com/jp/app/drop-ai-ai%E3%82%A4%E3%83%A9%E3%82%B9%E3%83%88-%E5%AE%9F%E5%86%99-%E9%A2%A8%E6%99%AF-ai%E7%94%BB%E5%83%8F%E7%94%9F%E6%88%90/id6749822141';

const featureTabs = [
  {
    id: 'image',
    label: 'AI画像生成',
    sectionLabel: 'AI IMAGE GENERATION',
    title: ['プロンプトを選ぶだけで', '高品質な画像を生成'],
    description: '豊富なプロンプトリストと作品テイスト選択で幅広い表現が可能。',
    visual: `${assetBase}/image-generation-feature.png`,
    visualAlt: '画像生成UI',
    features: [
      { icon: `${assetBase}/ai-image-tag.png`, title: 'プロンプトタグ選択', desc: 'スタイル・シーン・人物などタグを選ぶだけ' },
      { icon: `${assetBase}/ai-image-touch.png`, title: 'テイスト特化で生成できる', desc: 'イラスト・実写・人物など豊富な作風テイスト' },
      { icon: `${assetBase}/ai-image-reference.png`, title: '様々な生成手法が可能', desc: '参照画像生成や生成物のプロンプト編集が可能' },
    ],
  },
  {
    id: 'video',
    label: 'AI動画生成',
    sectionLabel: 'AI VIDEO GENERATION',
    title: ['Google Veo 3.1 搭載', 'AI動画をかんたん生成'],
    description: 'テキストプロンプトや参照画像から8秒のAI動画を生成。',
    video: `${assetBase}/video_sample.mp4`,
    features: [
      { icon: `${assetBase}/ai-video-generate.png`, title: 'テキスト→動画生成', desc: 'プロンプトだけで最大8秒の動画を生成' },
      { icon: `${assetBase}/ai-video-frame.png`, title: '最初・最後のフレーム指定', desc: '参照画像でシーンの始まりと終わりを指定' },
      { icon: `${assetBase}/ai-video-idea.png`, title: 'プロンプトアイデアで簡単生成', desc: 'プロンプトサンプルを参照してカンタン生成' },
    ],
  },
  {
    id: 'design',
    label: 'AIデザイン編集',
    sectionLabel: 'AI DESIGN',
    title: ['定形作業をワンタップで。', 'AIデザイン機能'],
    description: '各種AIデザインツールで画像編集をもっと速く、便利に。',
    beforeAfter: {
      before: `${assetBase}/af-background-removal.png`,
      after: `${assetBase}/bf-background-removal.png`,
      alt: '背景削除',
    },
    features: [
      { icon: `${assetBase}/ai-design-background-removal.png`, title: 'AI背景削除', desc: '商品・人物の背景をワンクリックで削除' },
      { icon: `${assetBase}/ai-design-upscale.png`, title: '高解像度化', desc: '低解像度画像を高解像度画像に変更' },
      { icon: `${assetBase}/ai-design-mannequin.png`, title: 'ゴーストマネキン / フラットレイ', desc: '衣服の着用感を人体なしでリアルに表現' },
    ],
  },
] as const;

const beforeAfterCards = [
  {
    icon: `${assetBase}/background-removal.png`,
    title: 'AI背景削除',
    desc: '商品・人物の背景をワンクリックで除去',
    before: `${assetBase}/af-background-removal.png`,
    after: `${assetBase}/bf-background-removal.png`,
  },
  {
    icon: `${assetBase}/upscaling.png`,
    title: '高解像度化',
    desc: '低解像度画像を最大4Kに引き伸ばし',
    before: `${assetBase}/af-upscaling.png`,
    after: `${assetBase}/bf-upscaling.png`,
  },
  {
    icon: `${assetBase}/ghost-mannequin.png`,
    title: 'ゴーストマネキン',
    desc: '人体なしで衣服の着用感をリアルに表現',
    before: `${assetBase}/af-ghost-mannequin.png`,
    after: `${assetBase}/bf-ghost-mannequin.png`,
  },
  {
    icon: `${assetBase}/old-photo-restoration.png`,
    title: '古写真の復活',
    desc: '色あせ・傷んだ写真をAIで鮮明に復元',
    before: `${assetBase}/af-old-photo-restoration.png`,
    after: `${assetBase}/bf-old-photo-restoration.png`,
  },
] as const;

const modelSections = [
  {
    title: '画像生成モデル',
    items: [
      { icon: `${assetBase}/Stable%20Diffusion%20XL.png`, name: 'Stable Diffusion XL', desc: 'Stability AIが提供する画像生成モデル。スピーディかつ多様な生成を実現。特化型のチェックポイントも豊富に提供可能。' },
      { icon: `${assetBase}/Nano%20Banana.png`, name: 'Nano Banana', desc: 'Googleが提供する最先端の画像生成モデル。生成速度と手軽さが魅力。' },
      { icon: `${assetBase}/Nano%20Banana.png`, name: 'Nano Banana Pro', desc: 'Googleが提供する最先端の画像生成モデル。「複数画像の組み合わせ」「テキスト出力」「高度な編集」など複雑なタスクに高い精度で対応可能。' },
    ],
  },
  {
    title: '動画生成モデル',
    items: [
      { icon: `${assetBase}/gemini-color.svg`, name: 'Veo 3.1 Lite', desc: 'Googleが提供する最先端の動画生成モデル。コストパフォーマンスよく動画を生成可能。' },
      { icon: `${assetBase}/gemini-color.svg`, name: 'Veo 3.1 Fast', desc: 'Googleが提供する最先端の動画生成モデル。高速で動画を生成可能なモード。' },
      { icon: `${assetBase}/gemini-color.svg`, name: 'Veo 3.1 Standard', desc: 'Googleが提供する最先端の動画生成モデル。品質重視で動画を生成可能なハイクオリティモード。' },
    ],
  },
] as const;

const useCases = [
  {
    title: 'ECサイト・アパレル担当者',
    desc: '商品撮影の後処理・背景削除・ゴーストマネキン・フラットレイをAIで自動化。スタジオ撮影コストを削減しながら、高品質な商品画像を量産できます。',
    tags: ['背景削除', 'ゴーストマネキン', 'フラットレイ', '高解像度化'],
  },
  {
    title: 'SNS・広告クリエイター',
    desc: '画像生成と動画生成をシームレスに切り替えながら、SNSや広告素材を効率的に制作できます。',
    tags: ['AI動画生成', 'Veo 3.1', 'SNSリール', '広告素材'],
  },
  {
    title: 'クリエイター',
    desc: 'プロンプト知識がなくても高品質なイラスト・アート素材を生成。インスピレーション機能で様々なクリエイティブプロンプトを参考にできます。',
    tags: ['Nano Banana Pro', '4K出力', 'インスピレーション'],
  },
] as const;

const faqs = [
  ['生成AIとは何ですか？', '生成AIとは、文章・画像・音声・動画・コードなどの大量データを学習し、利用者の指示に応じて新しいコンテンツや回答を自動生成するAI技術です。Drop AIでは、プロンプトをもとに画像や動画を生成するAIモデルを扱っています。'],
  ['Drop AIはどのようなサービスですか？', 'Drop AIは、AI画像生成・AI動画生成・AIデザイン編集ができるAIビジュアルエージェントサービスです。複数の先端AIモデルを利用できることに加え、充実したプロンプト補助機能から高品質なビジュアル生成ができます。'],
  ['無料プランと有料プランの違いは何ですか？', '無料プランは一部機能制限がありますが、費用を支払わずに画像生成ができます。有料プランでは生成制限の緩和や利用機能制限のUnlockなど、無料と比較して利用上のメリットが増えます。'],
  ['有料プランの場合に自動更新を停止することはできますか？', '有料プランの自動更新停止はアカウントページから操作可能です。自動更新の停止を希望される方は、自動更新がなされる前に更新停止のお手続きを完了してください。自動更新を停止された場合、有料プランの期間終了をもって当該プランが利用できなくなり、Freeプランにダウングレードされます。なお、有料プランは期間半ばでの途中解約および返金対応はできませんのでご注意ください。'],
  ['有料プランの支払い方法を教えてください', '有料プランの支払い方法はクレジットカード決済（VISA、MASTER、American Express）です。iOSアプリ版ではApp Store決済も利用可能です。'],
  ['Drop AIの生成は安全ですか？', 'Drop AIは、性的または暴力的な生成を最小限にするため、NSFW（Not safe for work）判定を実施しております。生成結果がNSFW判定に引っかかった場合は当該生成結果の閲覧はできません。あわせて生成クレジットも消化されません。もっとも、生成結果はプロンプトに影響されますので、実際の生成内容の安全性や適法性等は、お客様ご自身の責任でご確認いただく必要がございます。'],
  ['Drop 生成物の商用利用は可能ですか？', 'Drop AIは生成結果の商用利用が可能なモデルを利用していますので、生成結果が人に危害を加える内容、誤った内容、わいせつな内容、第三者の権利を侵害する内容、公序良俗に反する内容、法律や利用規約に違反する内容など不適切な内容でない限り、商用利用を禁じるものではございません。もっとも、生成結果は入力したプロンプトに影響されるため、既存の著作権、肖像権等の知的財産権その他第三者の権利に抵触しているか等を含め、実際の生成内容について商用利用が可能かは、お客様ご自身の責任でご確認いただく必要がございます。'],
  ['生成画像の著作権について教えてください。', '通常、生成AIで作成した画像や動画には著作権は生じません。ただし、生成時の指示内容により既存の著作権、肖像権等の知的財産権その他第三者の権利に抵触する場合はあり得ます。従いまして、プロンプトの内容や参照素材および実際の生成結果について、既存の著作権、肖像権等の知的財産権その他第三者の権利に抵触していないかどうかは、お客様ご自身の責任でご確認いただく必要がございます。'],
] as const;

type BeforeAfterProps = {
  before: string;
  after: string;
  alt: string;
  initial?: number;
  fill?: boolean;
};

const BeforeAfter: FC<BeforeAfterProps> = ({ before, after, alt, initial = 50, fill = false }) => {
  const [value, setValue] = useState(initial);

  return (
    <div className={`${styles.baWrap} ${fill ? styles.baWrapFill : ''}`}>
      <img className={`${styles.baImage} ${styles.baImageBefore}`} src={before} alt={`${alt} before`} />
      <img
        className={`${styles.baImage} ${styles.baImageAfter}`}
        src={after}
        alt={`${alt} after`}
        style={{ clipPath: `inset(0 ${100 - value}% 0 0)` }}
      />
      <div className={styles.baLabelBefore}>Before</div>
      <div className={styles.baLabelAfter}>After</div>
      <div className={styles.baDivider} style={{ left: `${value}%` }} />
      <div className={styles.baHandle} style={{ left: `${value}%` }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0909ff" strokeWidth="2.5">
          <path d="M8 4l-4 8 4 8M16 4l4 8-4 8" />
        </svg>
      </div>
      <input
        className={styles.baSlider}
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(event) => setValue(Number(event.target.value))}
        aria-label={`${alt}の比較スライダー`}
      />
    </div>
  );
};

export const TopPage: FC = () => {
  const location = useLocation();
  const heroRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [activeTab, setActiveTab] = useState<'image' | 'video' | 'design'>('image');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [showFixedCta, setShowFixedCta] = useState(false);

  const activeFeatureTab = useMemo(
    () => featureTabs.find((tab) => tab.id === activeTab) ?? featureTabs[0],
    [activeTab]
  );

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const top = element.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (activeTab === 'video') {
      void video.play().catch(() => undefined);
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, [activeTab]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowFixedCta(!entry.isIntersecting),
      { threshold: 0.2 }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Drop AI - AI動画もAI画像も生成できるビジュアルエージェント</title>
        <meta
          name="description"
          content="Drop AI（ドロップエーアイ）は、AI画像生成・AI動画生成・AIデザイン編集ができるAIビジュアルエージェントです。複数の先端生成AIモデルの利用に加え、充実したプロンプト補助機能から高品質なビジュアル生成ができます。"
        />
      </Helmet>

      <main className={styles.page}>
        <section ref={heroRef} className={styles.hero}>
          <div className={styles.heroInner}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroHeadline}>
                AI画像も
                <br />
                <span>AI動画も、</span>
                <br />
                高品質生成。
              </h1>
              <p className={styles.heroSub}>
                動画も画像も最新AIモデルを選んで使える。
                <br />
                クリエイターのためのAIビジュアル生成サービス
              </p>
              <div className={styles.badges}>
                <span className={styles.badge}>Stable Diffusion XL</span>
                <span className={styles.badge}>Nano Banana</span>
                <span className={styles.badge}>Nano Banana Pro</span>
                <span className={styles.badge}>Veo 3.1</span>
              </div>
              <div className={styles.heroActions}>
                <a href="/image-generation" className={`${styles.button} ${styles.buttonPrimary}`}>
                  <span className={styles.buttonInner}>
                    <span className={styles.buttonSpark} aria-hidden="true">✦</span>
                    <span>AI生成をはじめる</span>
                  </span>
                  <span className={styles.buttonArrow} aria-hidden="true">›</span>
                </a>
                <div className={styles.appStore}>
                  <p>＼ iOSアプリをチェック ／</p>
                  <a href={appStoreUrl} target="_blank" rel="noreferrer">
                    <img src={`${assetBase}/Download_on_the_App_Store_Badge_JP_RGB_blk_100317.svg`} alt="App Storeからダウンロード" />
                  </a>
                </div>
              </div>
            </div>
            <div className={styles.heroVisual}>
              <img className={styles.heroKv} src={`${assetBase}/KV1.svg`} alt="AI生成画像サンプルグリッド" />
            </div>
          </div>
        </section>

        <section id="cando" className={`${styles.section} ${styles.darkSection}`}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <div className={styles.sectionLabel}>FEATURES</div>
              <h2 className={`${styles.sectionTitle} ${styles.whiteText}`}>Drop AI でできること</h2>
              <p className={`${styles.sectionDesc} ${styles.whiteText} ${styles.desktopOnly}`}>画像・動画・AIデザインを、ひとつのサービスで完結</p>
            </div>

            <div className={styles.tabs}>
              {featureTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className={styles.featureGrid}>
              <div className={styles.featureLeftColumn}>
                <div>
                  <div className={styles.featureSectionLabel}>{activeFeatureTab.sectionLabel}</div>
                  <h3 className={`${styles.featureTitle} ${styles.whiteText}`}>
                    {activeFeatureTab.title[0]}
                    <br />
                    {activeFeatureTab.title[1]}
                  </h3>
                  <p className={`${styles.sectionDesc} ${styles.whiteText} ${styles.desktopOnly}`}>{activeFeatureTab.description}</p>
                </div>

                <div className={styles.featureList}>
                  {activeFeatureTab.features.map((feature) => (
                    <div key={feature.title} className={styles.featureCard}>
                      <img src={feature.icon} alt="" className={styles.featureIcon} />
                      <div>
                        <div className={styles.featureCardTitle}>{feature.title}</div>
                        <div className={styles.featureCardDesc}>{feature.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.featureVisual}>
                {activeFeatureTab.id === 'video' ? (
                  <video ref={videoRef} className={styles.featureVideo} src={activeFeatureTab.video} muted loop playsInline controls={false} />
                ) : activeFeatureTab.id === 'design' && activeFeatureTab.beforeAfter ? (
                  <BeforeAfter before={activeFeatureTab.beforeAfter.before} after={activeFeatureTab.beforeAfter.after} alt={activeFeatureTab.beforeAfter.alt} fill />
                ) : activeFeatureTab.id === 'image' ? (
                  <img src={activeFeatureTab.visual} alt={activeFeatureTab.visualAlt} />
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section id="aidesign" className={`${styles.section} ${styles.lightGradientSection}`}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <div className={styles.sectionLabel}>AI DESIGN</div>
              <h2 className={styles.sectionTitle}>AIデザイン機能でワンタップ編集</h2>
              <p className={`${styles.sectionDesc} ${styles.desktopOnly}`}>EC、フリマ、SNSなど各種写真の画像加工など定形作業をカンタン完結</p>
            </div>

            <div className={styles.beforeAfterGrid}>
              {beforeAfterCards.map((card) => (
                <article key={card.title} className={styles.beforeAfterCard}>
                  <div className={styles.beforeAfterCardHeader}>
                    <img src={card.icon} alt="" className={styles.beforeAfterCardIcon} />
                    <div>
                      <h3 className={styles.beforeAfterCardTitle}>{card.title}</h3>
                      <p className={styles.beforeAfterCardDesc}>{card.desc}</p>
                    </div>
                  </div>
                  <BeforeAfter before={card.before} after={card.after} alt={card.title} />
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="models" className={styles.section}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <div className={styles.sectionLabel}>AI MODELS</div>
              <h2 className={styles.sectionTitle}>最新のAIモデルを使える</h2>
              <p className={`${styles.sectionDesc} ${styles.desktopOnly}`}>最新の画像モデル、動画モデルをカンタンに切り替え可能</p>
            </div>

            <div className={styles.modelsWrap}>
              {modelSections.map((section) => (
                <div key={section.title}>
                  <div className={styles.modelColumnTitle}>{section.title}</div>
                  <div className={styles.modelList}>
                    {section.items.map((item) => (
                      <article key={item.name} className={styles.modelCard}>
                        <div className={styles.modelIconWrap}>
                          <img src={item.icon} alt={item.name} className={styles.modelIcon} />
                        </div>
                        <div>
                          <h3 className={styles.modelName}>{item.name}</h3>
                          <p className={styles.modelDesc}>{item.desc}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.gallerySection}`}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <div className={styles.sectionLabel}>INSPIRATION</div>
              <h2 className={`${styles.sectionTitle} ${styles.whiteText}`}>プロンプトアイデアを参照</h2>
              <p className={`${styles.sectionDesc} ${styles.whiteText} ${styles.sectionDescCentered}`}>豊富な画像コレクションから構図やプロンプトをヒントにできる</p>
            </div>
          </div>
          <img src={`${assetBase}/inspiration.png`} alt="AI生成画像ギャラリー" className={styles.galleryImage} />
          <div className={styles.container}>
            <div className={styles.galleryAction}>
              <a href="/inspiration" className={`${styles.button} ${styles.buttonOutline}`}>
                インスピレーション画像を見る
              </a>
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.lightGradientSection}`}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <div className={styles.sectionLabel}>USE CASES</div>
              <h2 className={styles.sectionTitle}>活用シーン</h2>
              <p className={`${styles.sectionDesc} ${styles.desktopOnly}`}>趣味・仕事・業種を問わず、幅広いシーンで利用可能</p>
            </div>
            <div className={styles.useCaseGrid}>
              {useCases.map((useCase) => (
                <article key={useCase.title} className={styles.useCaseCard}>
                  <h3 className={styles.useCaseTitle}>{useCase.title}</h3>
                  <p className={styles.useCaseDesc}>{useCase.desc}</p>
                  <div className={styles.useCaseTags}>
                    {useCase.tags.map((tag) => (
                      <span key={tag} className={styles.useCaseTag}>{tag}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className={`${styles.section} ${styles.ctaBand}`}>
          <div className={styles.container}>
            <div className={styles.ctaGrid}>
              <div className={styles.ctaLeft}>
                <h2 className={`${styles.sectionTitle} ${styles.whiteText}`}>高品質なAI生成の世界へ<br />まずは無料でトライ</h2>
                <div className={styles.ctaActions}>
                  <a href="/image-generation" className={`${styles.button} ${styles.buttonPrimary}`}>
                    <span className={styles.buttonInner}>
                      <span className={styles.buttonSpark} aria-hidden="true">✦</span>
                      <span>AI生成をはじめる</span>
                    </span>
                    <span className={styles.buttonArrow} aria-hidden="true">›</span>
                  </a>
                </div>
                <p className={styles.ctaNote}>
                  制限を気にせず使い倒したい方には有料プランがあります。
                  <br />
                  <a href="/plan">プラン一覧を見る</a>
                </p>
              </div>
              <div className={styles.priceBox}>
                <p className={styles.priceLabel}>Free</p>
                <p className={styles.priceAmount}><span>¥</span>0</p>
                <ul className={styles.priceList}>
                  <li>1日あたりの生成数の上限 4まで</li>
                  <li>広告非表示オプション なし</li>
                  <li>同時画像生成 1まで</li>
                  <li>生成画像の保存期間 30日間</li>
                  <li>詳細画像設定機能 なし</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className={styles.section}>
          <div className={styles.container}>
            <div className={styles.sectionHeaderCentered}>
              <h2 className={`${styles.sectionTitle} ${styles.faqTitle}`}>よくある質問</h2>
            </div>
            <div className={styles.faqList}>
              {faqs.map(([question, answer], index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div key={question} className={styles.faqItem}>
                    <button type="button" className={styles.faqQuestion} onClick={() => setOpenFaqIndex(isOpen ? null : index)}>
                      <span>{question}</span>
                      <span className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`} aria-hidden="true" />
                    </button>
                    <div className={`${styles.faqAnswer} ${isOpen ? styles.faqAnswerOpen : ''}`}>
                      <div className={styles.faqAnswerInner}>{answer}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className={`${styles.fixedCta} ${showFixedCta ? styles.fixedCtaVisible : ''}`}>
          <a href="/image-generation" className={`${styles.button} ${styles.buttonPrimary} ${styles.fixedCtaButton}`}>
            <span className={styles.buttonInner}>
              <span className={styles.buttonSpark} aria-hidden="true">✦</span>
              <span>AI生成をはじめる</span>
            </span>
            <span className={styles.buttonArrow} aria-hidden="true">›</span>
          </a>
        </div>
      </main>
    </>
  );
};
